<template>
  <v-card 
    id="market-signals-panel"
    :class="{ 'market-signals-panel-highlight': highlight }"
    class="market-signals-card mb-6" 
    elevation="0">
    <v-card-title class="pb-2">
      <h3 class="panel-title text-h6 font-weight-semibold d-flex align-center mb-0">
        <v-icon class="panel-title-icon mr-2">mdi-chart-line</v-icon>
        Market Signals
      </h3>
    </v-card-title>

    <v-card-text v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="#ff1463"></v-progress-circular>
    </v-card-text>

    <v-card-text v-else-if="!marketSignals || marketSignals.length === 0" class="empty-state-text text-center py-4">
      No market signals detected
    </v-card-text>

    <v-card-text v-else class="market-signals-container pa-4">
      <div 
        v-for="(marketSignal, index) in paginatedItems" 
        :key="index"
        class="market-signal-card pa-4 rounded"
        :class="{ 'market-signal-card--high-priority': marketSignal.priority === 'high', 'market-signal-card--clickable': hasSearchTarget(marketSignal) }"
        @click="handleCardClick(marketSignal)"
      >
        <div class="d-flex align-start gap-3 mb-3">
          <div 
            class="market-signal-icon-container"
            :class="{ 'market-signal-icon-container--high-priority': marketSignal.priority === 'high' }">
            <v-icon :color="getMarketSignalColor(marketSignal)" size="18">{{ getMarketSignalIcon(marketSignal) }}</v-icon>
          </div>
          <div class="flex-grow-1">
            <div class="d-flex align-center gap-2 mb-2">
              <h4 class="market-signal-title text-body-2 font-weight-semibold mb-0">{{ marketSignal.insight }}</h4>
              <v-icon v-if="marketSignal.priority === 'high'" medium color="#b71c1c" text-color="white">
                mdi-alert-circle
              </v-icon>
            </div>
            <p class="market-signal-description text-body mb-0">
              {{ getMarketSignalExplanation(marketSignal) }}
            </p>
          </div>
        </div>
        <div v-if="marketSignal.details" class="market-signal-details d-flex flex-wrap pt-3">
          <span v-if="marketSignal.details.contracts" class="d-flex align-center">
            <v-icon x-small class="market-signal-detail-icon mr-1">mdi-file-document-outline</v-icon>
            <span 
              v-if="marketSignal.details.tender_ids && marketSignal.details.tender_ids.length > 0"
              class="market-signal-link text-decoration-none"
            >
              {{ marketSignal.details.contracts }} contracts
            </span>
            <span v-else>{{ marketSignal.details.contracts }} contracts</span>
          </span>
          <span v-if="marketSignal.details.total_value || marketSignal.details.value" class="d-flex align-center">
            <v-icon x-small class="pattern-detail-icon mr-1">mdi-cash</v-icon>
            {{ formatCurrency(marketSignal.details.total_value || marketSignal.details.value) }}
          </span>
          <span v-if="marketSignal.type === 'category_surge' && marketSignal.details.from">
            From {{ marketSignal.details.from }} to {{ marketSignal.details.to }} contracts
          </span>
          <span 
            v-if="marketSignal.type === 'category_surge' && marketSignal.details.growth" 
            class="market-signal-growth font-weight-bold"
            :style="`--pattern-color: ${getMarketSignalColor(marketSignal)}`">
            {{ marketSignal.details.growth }}
          </span>
        </div>
      </div>
    </v-card-text>

    <!-- Pagination for Market Signals -->
    <pagination-controls
      :show="showPagination"
      :text="paginationText"
      :can-prev="canGoToPreviousPage"
      :can-next="canGoToNextPage"
      @prev="goToPreviousPage"
      @next="goToNextPage"
    />
  </v-card>
</template>

<script>
import PaginationMixin from './mixins/PaginationMixin.js';
import PaginationControls from './PaginationControls.vue';

export default {
  name: 'MarketSignalsPanel',
  
  mixins: [PaginationMixin],

  components: {
    PaginationControls
  },
  props: {
    marketSignals: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    // Optional: allow parent to control page size; falls back to 3
    itemsPerPage: {
      type: Number,
      default: 3
    },
    formatCurrency: {
      type: Function,
      required: true
    },
    highlight: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    // Items used by the shared pagination mixin
    paginationItems() {
      return this.marketSignals || [];
    }
  },

  methods: {
    hasSearchTarget(marketSignal) {
      return (
        marketSignal &&
        marketSignal.details &&
        Array.isArray(marketSignal.details.tender_ids) &&
        marketSignal.details.tender_ids.length > 0
      );
    },

    handleCardClick(marketSignal) {
      if (!this.hasSearchTarget(marketSignal)) return;
      const tenderIds = marketSignal.details.tender_ids;
      const url = this.getSearchUrl(tenderIds);
      // Open in a new tab, matching the previous router-link behavior
      window.open(url, '_blank');
    },

    getMarketSignalIcon(marketSignal) {
      if (marketSignal.type === 'consolidation_opportunity') return 'mdi-link';
      if (marketSignal.type === 'competitor_expansion') return 'mdi-trending-up';
      if (marketSignal.type === 'category_surge') return 'mdi-fire';
      return 'mdi-lightbulb';
    },

    getMarketSignalColor(marketSignal) {
      if (marketSignal.priority === 'high') return '#b71c1c';
      return '#ff1463';
    },

    getMarketSignalExplanation(marketSignal) {
      if (marketSignal.type === 'consolidation_opportunity') {
        return `This buyer might be consolidating suppliers. With ${(marketSignal.details && marketSignal.details.contracts) || marketSignal.count} contracts expiring close together, this could be an opportunity to win multiple contracts or a larger consolidated deal.`;
      } else if (marketSignal.type === 'competitor_expansion') {
        const supplierNames = (marketSignal.details && marketSignal.details.supplier_names) || [];
        if (supplierNames.length === 1) {
          return `Your competitor, ${supplierNames[0]}, is making moves in this space. This could indicate market growth or a strategic shift worth investigating.`;
        } else if (supplierNames.length <= 3) {
          const namesStr = supplierNames.slice(0, -1).join(', ') + ` and ${supplierNames[supplierNames.length - 1]}`;
          return `Your competitors, ${namesStr}, are making moves in this space. This could indicate market growth or a strategic shift worth investigating.`;
        } else if (supplierNames.length > 3) {
          const namesStr = supplierNames.slice(0, 2).join(', ') + ` and ${supplierNames.length - 2} others`;
          return `Your competitors, ${namesStr}, are making moves in this space. This could indicate market growth or a strategic shift worth investigating.`;
        }
        return `Your competitor is making moves in this space. This could indicate market growth or a strategic shift worth investigating.`;
      } else if (marketSignal.type === 'category_surge') {
        return `Activity in this category has increased significantly. This could indicate budget releases or upcoming opportunities.`;
      }
      return marketSignal.insight || '';
    },

    getSearchUrl(tenderIds) {
      if (!tenderIds || tenderIds.length === 0) {
        return '/search';
      }
      // Build query string with tender_id parameters
      const params = tenderIds.map(tid => `tender_id=${tid}`).join('&');
      return `/search?${params}&viewIndex=0`;
    }
  }
};
</script>

<style scoped>
/* Card Container */
.market-signals-card {
  border: 1px solid #e5e7eb;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
}

.market-signals-panel-highlight {
  animation: highlight-pulse 1.5s ease-in-out;
  position: relative;
  overflow: hidden;
}

.market-signals-panel-highlight::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 20, 99, 0.1);
  animation: highlight-fade 1.5s ease-in-out;
}

@keyframes highlight-pulse {
  0%, 100% { 
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    background-color: white;
  }
  50% { 
    box-shadow: 0 0 0 4px rgba(255, 20, 99, 0.2);
    background-color: #fff5f8;
  }
}

@keyframes highlight-fade {
  0%, 100% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
}

/* Title */
.panel-title {
  color: #1f2937;
}

.panel-title-icon {
  color: #6b7280;
}

/* Empty State */
.empty-state-text {
  color: #6b7280;
}

/* Market Signals Container */
.market-signals-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Market Signal Card */
.market-signal-card {
  border: 2px solid #fca5a5;
  background-color: #fef2f2;
  border-radius: 6px;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
}

.market-signal-card--clickable {
  cursor: pointer;
}

.market-signal-card--clickable:hover {
  background-color: #ffe4ea;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.market-signal-card--high-priority {
  border: 2px solid #ef5350;
  background-color: #ffebee;
}

/* Market Signal Icon Container */
.market-signal-icon-container {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffebf3;
  border-radius: 8px;
  flex-shrink: 0;
  margin-right: 12px;
}

.market-signal-icon-container--high-priority {
  background-color: #ffcdd2;
}

/* Market Signal Content */
.market-signal-title {
  color: #1f2937;
}

.market-signal-description {
  color: #6b7280;
  line-height: 1.6;
}

/* Market Signal Details */
.market-signal-details {
  gap: 16px;
  border-top: 1px solid #e5e7eb;
  font-size: 11px;
  color: #6b7280;
}

.market-signal-detail-icon {
  font-size: 14px;
  color: #6b7280;
}

.market-signal-link {
  color: #ff1463;
  font-weight: 600;
}

.market-signal-growth {
  color: var(--market-signal-color, #ff1463);
}

/* Pagination */
.pagination-container {
  border-top: 1px solid #e5e7eb;
  background-color: #f9fafb;
}

.pagination-wrapper {
  width: 100%;
}

.pagination-text {
  color: #6b7280;
}

.pagination-button {
  color: #6b7280;
}
</style>
